/**
 * Intermediate decimal text accepted while the user is typing. Both Spanish
 * comma and international dot separators are supported, but grouping
 * separators and exponent notation are intentionally excluded.
 */
const EDITABLE_DECIMAL_PATTERN = /^-?\d*(?:[.,]\d*)?$/;

export function isEditableDecimalInput(value: string): boolean {
	return EDITABLE_DECIMAL_PATTERN.test(value);
}

/** Canonical representation used by Angular forms and API payload builders. */
export function normalizeDecimalInput(value: string): string {
	return value.replace(',', '.');
}

/**
 * Parses a decimal entered with comma or dot. Invalid and incomplete values
 * return null so callers never submit NaN.
 */
export function parseDecimalInput(value: unknown): number | null {
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value !== 'string') return null;

	const trimmed = value.trim();
	if (!trimmed || !isEditableDecimalInput(trimmed) || !/\d/.test(trimmed)) return null;

	const parsed = Number(normalizeDecimalInput(trimmed));
	return Number.isFinite(parsed) ? parsed : null;
}

/** Spanish display for values written programmatically into decimal fields. */
export function formatDecimalInput(value: unknown): string {
	if (value === null || value === undefined || value === '') return '';
	return String(value).replace('.', ',');
}

/** Adds or subtracts a step without exposing floating-point artefacts. */
export function stepDecimalInput(value: number, step: number, direction: 1 | -1): number {
	const fractionDigits = (number: number) => {
		const [, fraction = ''] = String(number).split('.');
		return fraction.length;
	};
	const precision = Math.min(Math.max(fractionDigits(value), fractionDigits(step)), 12);
	return Number((value + step * direction).toFixed(precision));
}
